import { Check, ChevronDown, Columns3Cog, X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface Option {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  group?: string;
}

interface OptionGroup {
  label: string;
  options: Option[];
}

interface StaticMultiSelectProps {
  options?: Option[];
  groups?: OptionGroup[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  clearAllLabel?: string;
  className?: string;
  disabled?: boolean;
  maxCount?: number;
  onMaxCountExceeded?: (maxCount: number) => void;
  showSelectedCount?: boolean;
  showClearAll?: boolean;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

// Multi-select variant where the trigger button text stays fixed regardless of selections made The trigger remains constant, selections dont show up on trigger
export function StaticMultiSelect({
  options = [],
  groups = [],
  selected,
  onChange,
  placeholder = 'Select items',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found',
  clearAllLabel = 'Clear all',
  className,
  disabled = false,
  maxCount,
  onMaxCountExceeded,
  showSelectedCount = false,
  showClearAll = true,
  variant = 'outline',
  size = 'default',
}: StaticMultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (value: string) => {
    if (disabled) return;

    if (selected.includes(value)) {
      onChange(selected.filter(item => item !== value));
    } else {
      if (maxCount && selected.length >= maxCount) {
        onMaxCountExceeded?.(maxCount);
        return;
      }
      onChange([...selected, value]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const selectedCount = selected.length;
  const hasSelected = selectedCount > 0;

  // Organize options into groups
  const organizedGroups = React.useMemo(() => {
    if (groups.length > 0) {
      return groups;
    }

    if (options.length > 0) {
      // Group options by their group property or put in default group
      const groupMap = new Map<string, Option[]>();

      options.forEach(option => {
        const groupKey = option.group || 'default';
        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, []);
        }
        groupMap.get(groupKey)!.push(option);
      });

      return Array.from(groupMap.entries()).map(([label, options]) => ({
        label: label === 'default' ? '' : label,
        options,
      }));
    }

    return [];
  }, [options, groups]);

  const renderOption = (option: Option) => {
    const isSelected = selected.includes(option.value);
    return (
      <CommandItem
        key={option.value}
        value={option.label}
        onSelect={() => handleSelect(option.value)}
        className="flex items-center justify-between"
      >
        <div className="flex items-center">
          {option.icon && <option.icon className="mr-2 h-4 w-4" />}
          <span>{option.label}</span>
        </div>
        <Check className={cn('ml-auto h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          role="combobox"
          aria-expanded={open}
          className={cn('justify-between', className)}
          disabled={disabled}
        >
          <Columns3Cog className="mr-2 h-4 w-4" />
          <span className="truncate">
            {placeholder}
            {showSelectedCount && hasSelected && ` (${selectedCount})`}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {organizedGroups.map((group, index) => (
              <CommandGroup key={group.label || `group-${index}`} heading={group.label}>
                {group.options.map(option => renderOption(option))}
              </CommandGroup>
            ))}
            {showClearAll && hasSelected && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem onSelect={handleClearAll}>
                    <X className="mr-2 h-4 w-4" />
                    {clearAllLabel} ({selectedCount})
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
